/**
 * Prefill de Cant. en el modal NC de Ventas → Listado.
 *
 * Total: restante acreditable (facturada − ya acreditada).
 * Parcial: no entregado del remito abierto (cantidad_facturada −
 * cantidad_entregada), acotado al restante. Sin remito: mismo restante.
 * F7: combo visual 1:1 — Cant. N = N de cada SKU.
 */

import {
  expandNcComboQtyMap,
  groupComboVisualRows,
} from "@/lib/comboLine";

export type NcFacturaItem = {
  id?: number;
  cantidad?: number;
  cantidadRestante?: number;
  cantidad_restante?: number;
  cantidadPendienteEntrega?: number;
  cantidad_pendiente_entrega?: number;
  comboGroup?: string | null;
  combo_group?: string | null;
  nombre?: string;
  nombreProducto?: string;
  nombre_producto?: string;
  precio_unitario?: number | string;
  precioUnitario?: number | string;
  descuento1?: number | string;
  descuento2?: number | string;
};

export type NcItemSelection = { selected: boolean; cantidad: number };

function toQty(raw: unknown, fallback: number): number {
  if (raw === undefined || raw === null || raw === "") return fallback;
  const n = Number(raw);
  if (Number.isNaN(n)) return fallback;
  return n;
}

/** Cantidad aún acreditable por línea (facturada − ya acreditada en NCs previas). */
export function ncCantidadRestante(item: NcFacturaItem): number {
  const original = Number(item.cantidad || 0);
  const restante = toQty(
    item.cantidadRestante ?? item.cantidad_restante,
    original,
  );
  return Math.max(0, Math.min(original, restante));
}

/**
 * Qty no entregada del remito (P8-2). Fallback: restante de factura.
 * Nunca deja vacío si se puede conocer.
 */
export function ncCantidadPendienteEntrega(item: NcFacturaItem): number {
  const restante = ncCantidadRestante(item);
  const raw = item.cantidadPendienteEntrega ?? item.cantidad_pendiente_entrega;
  if (raw === undefined || raw === null || raw === "") return restante;
  const pend = toQty(raw, restante);
  return Math.max(0, Math.min(restante, pend));
}

/** Prefill de Cant. según Total vs Parcial. No bloquea el input. */
export function ncCantidadSugerida(
  item: NcFacturaItem,
  parcial: boolean,
): number {
  return parcial ? ncCantidadPendienteEntrega(item) : ncCantidadRestante(item);
}

/** F7: una Cant. visual = min de las dos patas (no superar la más chica). */
export function ncComboRestante(legs: NcFacturaItem[]): number {
  if (!legs.length) return 0;
  return Math.min(...legs.map((leg) => ncCantidadRestante(leg)));
}

export function ncComboSugerida(legs: NcFacturaItem[], parcial: boolean): number {
  if (!legs.length) return 0;
  return Math.min(...legs.map((leg) => ncCantidadSugerida(leg, parcial)));
}

export function ncComboPendiente(legs: NcFacturaItem[]): number {
  if (!legs.length) return 0;
  return Math.min(...legs.map((leg) => ncCantidadPendienteEntrega(leg)));
}

export function ncItemIds(legs: NcFacturaItem[]): number[] {
  return legs
    .map((leg) => Number(leg.id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function syncNcComboItemMap(
  items: Array<NcFacturaItem & Record<string, unknown>> | undefined,
  map: Record<number, NcItemSelection>,
  reduce: "min" | "max" = "min",
): Record<number, NcItemSelection> {
  const qtyMap: Record<number, number> = {};
  for (const [id, sel] of Object.entries(map)) {
    qtyMap[Number(id)] = sel.cantidad;
  }
  const syncedQty = expandNcComboQtyMap(items, qtyMap, reduce);
  const next: Record<number, NcItemSelection> = { ...map };
  const rows = groupComboVisualRows(items || []);
  for (const row of rows) {
    if (!row.isCombo) continue;
    const ids = ncItemIds(row.legs);
    if (!ids.length) continue;
    const selected = ids.some((id) => next[id]?.selected);
    const cap = ncComboRestante(row.legs);
    const raw = ids
      .map((id) => syncedQty[id] ?? next[id]?.cantidad ?? 0)
      .filter((n) => Number.isFinite(n));
    if (!raw.length) continue;
    const reduced = reduce === "min" ? Math.min(...raw) : Math.max(...raw);
    const qty = Math.max(0, Math.min(cap, reduced));
    for (const id of ids) {
      next[id] = { selected, cantidad: qty };
    }
  }
  return next;
}

export function patchNcVisualSelection(
  items: Array<NcFacturaItem & Record<string, unknown>> | undefined,
  map: Record<number, NcItemSelection>,
  legIds: number[],
  patch: Partial<NcItemSelection>,
): Record<number, NcItemSelection> {
  const next: Record<number, NcItemSelection> = { ...map };
  for (const id of legIds) {
    const prev = next[id] || { selected: false, cantidad: 0 };
    next[id] = {
      selected: patch.selected ?? prev.selected,
      cantidad: patch.cantidad ?? prev.cantidad,
    };
  }
  return syncNcComboItemMap(items, next, "max");
}

export function ncPayloadFromSelection(
  items: Array<NcFacturaItem & Record<string, unknown>> | undefined,
  map: Record<number, NcItemSelection>,
): Array<{ id: number; cantidad: number }> {
  const synced = syncNcComboItemMap(items, map, "max");
  const qty: Record<number, number> = {};
  for (const [id, sel] of Object.entries(synced)) {
    if (sel.selected && sel.cantidad > 0) qty[Number(id)] = sel.cantidad;
  }
  const expanded = expandNcComboQtyMap(items, qty, "max");
  return Object.entries(expanded)
    .filter(([, cantidad]) => cantidad > 0)
    .map(([id, cantidad]) => ({ id: Number(id), cantidad }));
}

export function buildNcItemMap(
  items: Array<NcFacturaItem & Record<string, unknown>> | undefined,
  opts?: { parcial?: boolean },
): Record<number, NcItemSelection> {
  const parcial = Boolean(opts?.parcial);
  const itemMap: Record<number, NcItemSelection> = {};
  (items || []).forEach((item) => {
    if (!item?.id) return;
    const cantidad = ncCantidadSugerida(item, parcial);
    const restante = ncCantidadRestante(item);
    itemMap[item.id] = {
      selected: parcial ? cantidad > 0 : restante > 0,
      cantidad,
    };
  });
  return syncNcComboItemMap(items, itemMap, "min");
}

export function ncVisualLineSubtotal(
  legs: NcFacturaItem[],
  cantidad: number,
): number {
  return legs.reduce((total, item) => {
    const precio = Number(item.precio_unitario || item.precioUnitario || 0);
    const desc1 = Number(item.descuento1 || 0);
    const desc2 = Number(item.descuento2 || 0);
    const sub = precio * cantidad;
    const d1 = desc1 > 0 ? sub * desc1 / 100 : 0;
    const a1 = sub - d1;
    const d2 = desc2 > 0 ? a1 * desc2 / 100 : 0;
    return total + (a1 - d2);
  }, 0);
}
